import { useHidePriceTypes, useTelesalesCustomerNumber, useUser } from "@tm/context-distribution"
import Morpheus from "@tm/morpheus"
import { Dictionary, useExternalCatalogUrl } from "@tm/utils"
import { useCallback, useEffect, useMemo, useState } from "react"
import { ArticleListV2DisplayBehavior, ListOptions } from "@tm/models"
import { useArticleListConfiguration } from "../../ArticleListConfiguration"
import { usePartsViewOptions } from "../../hooks/usePartsViewOptions"
import { useListParamsContext } from "../ListParams"
import { useMapTileDisplayBehaviorSettingsKey } from "../../helpers"

/** @todo Merge somehow with `ArticleListConfiguration` */
export function useListOptions(): ListOptions {
    const { addToBasketExternalSystemId } = Morpheus.getParams("basket")
    const { isPriceTypeHidden } = useHidePriceTypes()
    const { telesalesCustomerNo } = useTelesalesCustomerNumber()
    const { externalCatalogUrl } = useExternalCatalogUrl({
        externalSystemId: addToBasketExternalSystemId,
        telesalesCustomerNo,
    })
    const listType = useListParamsContext((x) => x.listType)
    const { userContext } = useUser()

    const [expandedArticleIds, setExpandedArticleIds] = useState<Dictionary<boolean>>({})

    const {
        showDataSupplierLogos,
        hasFavoriteArticles,
        hideCostEstimationButton,
        hideAddToBasketButton,
        highlightWatchlistButton,
        hasNarrowRightActionsContainer,
        erpDataCustomization,
    } = useArticleListConfiguration()
    const mapTileDisplayBehaviorSettingsKey = useMapTileDisplayBehaviorSettingsKey()
    const { showPurchasePrice, partsViewSettings } = usePartsViewOptions()
    const { showArticleImages, compactView, hideProductGroupsInUniSearch, hideProductGroupsInOffersList } = partsViewSettings

    const tileView = useMemo(() => {
        const key = mapTileDisplayBehaviorSettingsKey(listType, userContext, true)
        if (key) {
            return partsViewSettings[key] === ArticleListV2DisplayBehavior.Tile
        }
        return false
    }, [listType, mapTileDisplayBehaviorSettingsKey, partsViewSettings, userContext])

    const hideProductGroupHeader = useMemo(() => {
        switch (listType) {
            case "offers":
                return tileView && hideProductGroupsInOffersList
            case "universal":
                return tileView && hideProductGroupsInUniSearch

            default:
                return false
        }
    }, [hideProductGroupsInOffersList, hideProductGroupsInUniSearch, listType, tileView])

    useEffect(() => {
        setExpandedArticleIds({})
    }, [compactView, tileView])

    const setExpandedArticleId = useCallback(
        (articleId: string) => {
            if (tileView) {
                setExpandedArticleIds((prev) => {
                    if (prev[articleId]) {
                        return {}
                    }
                    return { [articleId]: true }
                })
                return
            }

            setExpandedArticleIds((prev) => {
                return { ...prev, [articleId]: !prev[articleId] }
            })
        },
        [tileView]
    )

    return useMemo<ListOptions>(
        () => ({
            showDocumentsInline: false,
            showDocumentsInCompact: false,
            openDocumentsAsModal: true,
            showReferenceLinksInCompact: false,
            showDataSupplierLogos,
            externalCatalogUrl,
            hasFavoriteArticles,
            hideCostEstimationButton: hideCostEstimationButton || listType === "universal" || listType === "offers",
            hideAddToBasketButton,
            highlightFavoriteArticlesButton: highlightWatchlistButton,
            showPurchasePrice,
            showArticleImages,
            isPriceTypeHidden,
            hasNarrowRightActionsContainer,
            compactView,
            tileView,
            erpDataCustomization,
            expandedArticleIds,
            setExpandedArticleId,
            hideProductGroupHeader,
        }),
        [
            showDataSupplierLogos,
            externalCatalogUrl,
            hasFavoriteArticles,
            hideCostEstimationButton,
            listType,
            hideAddToBasketButton,
            highlightWatchlistButton,
            showPurchasePrice,
            showArticleImages,
            isPriceTypeHidden,
            hasNarrowRightActionsContainer,
            compactView,
            tileView,
            erpDataCustomization,
            expandedArticleIds,
            setExpandedArticleId,
            hideProductGroupHeader,
        ]
    )
}
